import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { get, map, size, initial, last, noop } from 'lodash';

// local dependencies
import { withNetworkState } from 'ui/hoc/withNetworkState';
import { CALL_ACTIVITY_STATE } from 'constants/common';
import { Icon } from 'modules/icon-bank';
import { button as Button } from 'modules/button';
import { getLabel } from 'utils/getLabel';
import { OutsideClickHandler } from 'vendors/OutsideClickHandler';
import { CallDispositionReasonsWidget } from 'ui/containers/CallDispositionReasonsWidget';
import { CallDispositionActivityWidget } from 'ui/containers/CallDispositionActivityWidget';
import { CallDispositionNotesWidget } from 'ui/containers/CallDispositionNotesWidget';
import { CallDispositionCloseCallWidget } from 'ui/containers/CallDispositionCloseCallWidget';
import { callTimer as CallTimer } from 'ui/views/generic/callTimer';
import { WIDGET_NAMES } from 'constants/entity-names';
import { getCallControlActionsResolver } from 'resolvers/getCallControlActions';

// call disposition categories -> widget views
const WIDGET_VIEWS = {
    [ WIDGET_NAMES.CALL_DISPOSITION_REASONS ]: CallDispositionReasonsWidget,
    [ WIDGET_NAMES.CALL_DISPOSITION_ACTIVITY ]: CallDispositionActivityWidget,
    [ WIDGET_NAMES.CALL_DISPOSITION_NOTES ]: CallDispositionNotesWidget,
};

// call disposition categories -> icon names
const ICON_NAMES = {
    callDispositionReasons: 'phoneMessage',
    callDispositionToDos: '',
    callDispositionNotes: 'callNotes',
    callDispositionActivity: 'callActivity',
};

// call constrol icon names
const CALL_CONTROL_ICON_NAMES = {
    endCall: 'endCall',
    transferCall: 'transferCall',
    retrieveCall: 'transferCall'
};

/*******************************/

// call `inactive` state view
const CallInactiveStateView = ( props ) => {
    return (
        <div className='ui-call-control-widget-inactive'>
            <div className='ui-call-control-widget-inactive__call-init-actions'>
                {
                    props.callInitiationActions.map( action => {

                        // get icon id
                        const iconId = 'initiateInboundCall' === action.name ? 'inboundCall' : 'outboundCall';

                        return (
                            <Button
                                key={ action.name }
                                className='ui-call-control-widget-inactive__call-init-actions__button'
                                title={ action.title }
                                size='large'
                                showIcon={ true }
                                iconId={ iconId }
                                onClick={ () => props.onCallControlActionClick( action ) }
                            />
                        );
                    } )
                }
            </div>
        </div>
    );
};

// set display name
CallInactiveStateView.displayName = 'callControlWidget.CallInactiveStateView';

/*---------------------------*/

// call `paused` state view
const CallPausedStateView = ( props ) => {
    return (
        <div className='ui-call-control-widget-paused'>
            {/* timer */}
            <div className='ui-call-control-widget-paused__timer'>
                <CallTimer startTime={ get( props, 'callState.callStartTime' ) } />
            </div>

            {/* actions */}
            <div className='ui-call-control-widget-paused__actions'>
                {
                    map( props.callResumeActions, action => {

                        // get icon name
                        const iconName = 'resumeCall' === action.name ? 'phone' : null;

                        return (
                            <Button
                                key={ action.name }
                                className='ui-call-control-widget-paused__actions__item'
                                title={ action.title }
                                size='extra-small'
                                showIcon={ true }
                                iconId={ iconName }
                                iconWidth='14'
                                iconHeight='14'
                            />
                        );
                    } )
                }
            </div>
        </div>
    );
};

// set display name
CallPausedStateView.displayName = 'callControlWidget.CallPausedStateView';

/*---------------------------*/

// call `active` state view
const CallActiveStateView = ( props ) => {

    // visibility state
    const [ hidden, setHidden ] = useState( ! props.hasCallEnded );
    useEffect( () => setHidden( ! props.hasCallEnded ), [ props.hasCallEnded ] );

    // extract widgets
    const callDataWidgets = initial( props.callDispositionWidgets );
    const callCloseWidget = last( props.callDispositionWidgets );
    const patientId = ( props.callState && props.callState.callerInfo && props.callState.callerInfo.patientId)
        ? props.callState.callerInfo.patientId
        : null;

    // get width of each section
    const sectionWidth = `${ 100 / size( callDataWidgets ) }%`;
    
    // call info class name
    const callInfoClassName = classnames( 'ui-call-control-widget-active__call-info', {
        'ui-call-control-widget-active__call-info--ended': props.hasCallEnded,
    } );

    // categories class name
    const categoriesClassName = classnames( 'ui-call-control-widget-active__call-disposition__categories', {
        'ui-call-control-widget-active__call-disposition__categories--data-panel-hidden': hidden,
    } );

    // data panel class name
    const dataPanelClassName = classnames( 'ui-call-control-widget-active__call-disposition__data-panel', {
        'ui-call-control-widget-active__call-disposition__data-panel--hidden': hidden,
    } );

    const callControlActions = getCallControlActionsResolver( props.callControlActions );

    return (
        <div className='ui-call-control-widget-active'>
            {/* left widget : call info */}
            <div className={ callInfoClassName }>

                {/* duration label */}
                {
                    props.hasCallEnded ? (
                        <p className='ui-call-control-widget-active__call-info__duration'>
                            { getLabel( 'displayTextDuration' ) }
                        </p>
                    ) : null
                }
               
                {/* timer */}
                <div className='ui-call-control-widget-active__call-info__timer'>
                    {
                        props.hasCallEnded ? (
                            <CallTimer startTime={ get( props, 'callState.callStartTime' ) } endTime={ get( props, 'callState.callEndTime' ) } />
                        ) : (
                            <CallTimer startTime={ get( props, 'callState.callStartTime' ) } />
                        )
                    }
                </div>

                {/* actions: only when call is active */}
                {
                    props.hasCallEnded ? null : (
                        <div className='ui-call-control-widget-active__call-info__actions'>
                            {
                                map( callControlActions, action => {

                                    // get icon name
                                    const iconName = CALL_CONTROL_ICON_NAMES[ action.name ];

                                    // dynamic theme
                                    const buttonTheme = 'endCall' === action.name ? 'danger' : null;

                                    return (
                                        <Button
                                            key={ action.name }
                                            className='ui-call-control-widget-active__call-info__actions__item'
                                            title={ action.title }
                                            size='extra-small'
                                            showIcon={ true }
                                            iconId={ iconName }
                                            iconWidth='16'
                                            iconHeight='16'
                                            iconFill={ 'danger' === buttonTheme ? '#ffffff' : undefined }
                                            theme={ buttonTheme }
                                            outline={  'danger' === buttonTheme ? false : true }
                                            onClick={ () => props.onCallControlActionClick( action ) }
                                        />
                                    );
                                } )
                            }
                        </div>
                    )
                }
            </div>

            {/* right widget : call disposition */}
            <div className='ui-call-control-widget-active__call-disposition'>

                {/* categories */}
                <div className={ categoriesClassName } data-ignore-outclick='true' onClick={ () => props.hasCallEnded ? noop : setHidden( false ) }>
                    {
                        map( callDataWidgets, widget => {
                            return (
                                <div key={ widget.name } className='ui-call-control-widget-active__call-disposition__categories__item' style={{ width: sectionWidth }}>

                                    {/* icon */}
                                    <div className='ui-call-control-widget-active__call-disposition__categories__item__icon'>
                                        <Icon name={ ICON_NAMES[ widget.name ] } />
                                    </div>

                                    {/* title */}
                                    <div className='ui-call-control-widget-active__call-disposition__categories__item__title'>
                                        { getLabel( widget.title ) }
                                    </div>
                                </div>
                            );
                        } )
                    }
                </div>
                
                {/* data panel (always when call has ended) */}
                { !hidden ? (
                    <OutsideClickHandler disabled={ hidden || props.hasCallEnded } onOutsideClick={ () => setHidden( true ) }>
                    <div className={ dataPanelClassName }>

                        {/* call data widgets */}
                        <div className='ui-call-control-widget-active__call-disposition__data-panel__call-data-widgets'>
                            {
                                map( callDataWidgets, widget => {

                                    // get widget view
                                    const WidgetView = WIDGET_VIEWS[ widget.name ];
                                    
                                    return (
                                        <div key={ widget.name } className='ui-call-control-widget-active__call-disposition__data-panel__call-data-widgets__item' style={{ width: sectionWidth }}>
                                            <WidgetView
                                                widget={ widget }
                                                onCallCategoryAndReasonsChange={ props.onCallCategoryAndReasonsChange }
                                                onCallNotesChange={ props.onCallNotesChange }
                                                memberId={ props.memberId }
                                                patientId = { patientId }
                                            />
                                        </div>
                                    );
                                } )
                            }
                        </div>

                        {/* call close widget */}
                        <CallDispositionCloseCallWidget
                            className='ui-call-control-widget-active__call-disposition__data-panel__call-close-widget'
                            widget={ callCloseWidget }
                            patientId = { patientId }
                            callReasonValidationState= { props.callReasonValidationState }
                            callNotesValidationState={ props.callNotesValidationState }
                            autoTaggingCallResolution= { props.autoTaggingCallResolution }
                        />
                    </div>
                </OutsideClickHandler>
                ) : null }
            </div>
        </div>
    );
};

// set display name
CallActiveStateView.displayName = 'callControlWidget.CallActiveStateView';

// default props
CallActiveStateView.defaultProps = {
    hasCallEnded: false,
};

/*******************************/

// success view
export const callControlWidgetSuccess = ( props ) => {

    // get state of the call activity
    const callActivityState = get( props.callState, 'callState', CALL_ACTIVITY_STATE.INACTIVE );

    // render a view based on call activity state
    switch( callActivityState ) {

        // when MSR is authorizing the caller/callee
        case CALL_ACTIVITY_STATE.AUTHORIZING:
        case CALL_ACTIVITY_STATE.VERIFYING:
        case CALL_ACTIVITY_STATE.ACTIVE:
        case CALL_ACTIVITY_STATE.ENDED: {
            return (
                <CallActiveStateView
                    { ...props }
                    hasCallEnded = { callActivityState === CALL_ACTIVITY_STATE.ENDED }
                    onCallControlActionClick = { props.onCallControlActionClick }
                    callReasonValidationState = { props.callReasonValidationState }
                />
            );
        }

        // when call is paused
        case CALL_ACTIVITY_STATE.PAUSED: {
            return (
                <CallPausedStateView { ...props } />
            );
        }

        // when no call is ongoing at the moment
        default:
        case CALL_ACTIVITY_STATE.INACTIVE: {
            return (
                <CallInactiveStateView
                    { ...props }
                />
            );
        }
    }
};

// set display name
callControlWidgetSuccess.displayName = 'callControlWidgetSuccess';

// set default props
callControlWidgetSuccess.defaultProps = {
    callState: {},
    callInitiationActions: [],
    callControlActions: [],
    callDispositionActions: [],
    callDispositionWidgets: [],
    onCallControlActionClick: noop,
};

// prop types
callControlWidgetSuccess.propTypes = {};

/*******************************/

// preloader view
export const callControlWidgetPreloader = () => {
    return (
        <div className='ui-call-control-widget-preloader'>
            <div className='ui-call-control-widget-preloader__left'>
                <div className='ui-call-control-widget-preloader__left__text' />
                <div className='ui-call-control-widget-preloader__left__button' />
            </div>

            <div className='ui-call-control-widget-preloader__right'>
                {
                    map( [ 1, 2 ], item => {
                        return (
                            <div key={ item } className='ui-call-control-widget-preloader__right__panel'>
                                <div className='ui-call-control-widget-preloader__right__panel__icon' />
                                <div className='ui-call-control-widget-preloader__right__panel__text' />
                            </div>
                        );
                    } )
                }
            </div>
        </div>
    );
};

// set display name
callControlWidgetPreloader.displayName = 'callControlWidgetPreloader';

// set default props
callControlWidgetPreloader.defaultProps = {};

// prop types
callControlWidgetPreloader.propTypes = {};

/*******************************/

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { callControlWidget as CallControlWidget } from 'ui/views/widgets/callControlWidget';
 *
 * // render
 * <CallControlWidget propName='propValue'/>
 */
export const callControlWidget = withNetworkState( callControlWidgetPreloader, callControlWidgetSuccess );
